/*@flow*/
import * as I from 'immutable';
import _ from 'lodash';
import { asks } from 'fp-ts/lib/Reader';
import type { Reader } from 'fp-ts/lib/Reader';
import type { List, Map } from 'immutable';
import type { LegendT } from './Stack';
import pluralize from 'pluralize';
import useContextState from 'comps/Left2/useContextState';
import unique from 'lib/unique';
import history from 'lib/history';

export type DeckT = {
  status: string,
  page_type: string,
  category: string,
  fillColor: string,
  strokeColor: string,
  defaultStrokeColor: string,
};

export type LayerT = {
  layergroup: string,
};

export type Vars = {
  decks: List<DeckT>,
  layers: List<LayerT>,
};

const deckKeys = {
  status: null,
  page_type: null,
  category: null,
  fillColor: null,
  strokeColor: null,
  defaultStrokeColor: null,
};

// HACK: enums are incompatible with strings in Flow, and we
// need a string key to index the map.
type LayerListMapKey = $Keys<typeof deckKeys> | string;
type LayerList = List<Map<LayerListMapKey, *>>;
const deckToLayerList = (coll: DeckT[]): LayerList => {
  const reducer = (s, v) => {
    // Pick only a subset of the keys from deck
    const keys = Object.keys(deckKeys);
    const subset = I.Map(v).filter((_2, k) => keys.includes(k));
    // Remove empty fields from Map
    const o = subset.filter(Boolean);
    // Add Map to Set unless it's empty/
    return o.isEmpty() ? s : s.add(o);
  };
  // Reduce over a set. And convert to List.
  return coll.reduce(reducer, I.Set()).toList();
};

type MyReader<A> = Reader<Vars, A>;

let cache = I.Map();

const addCircle = (prefaceItems) => ([label, color]) => {
  if (color && color !== 'white') {
    prefaceItems.push({
      dotStyle: 'stroke',
      dotType: 'circle',
      color,
      label,
    });
  }
};

const titleMap = {
  Tenant: 'Businesses & Attractions',
  Development: 'Development Activity',
  Listing: 'Real Estate Listings',
  StreetscapeAsset: 'Streetscape Assets',
  StreetscapeIssue: 'Streetscape Issues',
  Event: 'Events',
  Unit: 'Units',
  Contact: 'Contacts',
  Property: 'Properties',
  Building: 'Buildings',
};

const getStrokeItems = (items) => {
  return items
    .map((v) => [
      v.get('status'),
      v.get('strokeColor'),
      v.get('defaultStrokeColor'),
    ])
    .filter((v) => v[0] && v[1] && v[1] !== v[2])
    .update((v) => {
      // Keep a unique item for each status.
      return v.update((v2) => {
        return v2
          .groupBy((v3) => v3[0])
          .map((v3, k) => {
            // Take the color of first item in the group. Since
            // they all have the same color, it shouldn't matter
            // which one we use.
            const color = v3.map((v4) => v4[1]).get(0);
            return I.fromJS([k, color]);
          })
          .toList();
      });
    })
    .toJS();
};

const getCategories = (items) => {
  return items
    .map((v2) => v2.get('category'))
    .update((v2) => v2.filterNot((v3, i) => v2.indexOf(v3) !== i))
    .sortBy((v2) => v2)
    .toJS()
    .filter(Boolean);
};

const getFillColorsForDomain = (items, colorDomain) =>
  items
    .filter((v2) => Boolean(v2.get('fillColor')))
    .reduce((s, v2) => s.set(v2.get('category'), v2.get('fillColor')), I.Map())
    .update((v) => colorDomain.map<string>((v2) => v.get(v2) || 'undefined'));

export const mkDeckLegendItem = (
  items: List<Map<*, *>>,
  name: string
): {|
  colorDomain: any,
  colorRange: any,
  dotStyle: string,
  prefaceItems: Array<
    any | {| color: mixed, dotStyle: string, dotType: string, label: mixed |}
  >,
  title: any,
  type: string,
|} => {
  let colorDomain = items
    .map((v2) => v2.get('status'))
    .filter((v) => Boolean(v))
    .filterNot((v) => v === '--')
    .toJS();
  let colorRange = items.map((v2) => v2.get('fillColor')).toJS();

  let prefaceItems = [];
  if (name === 'Listing') {
    colorDomain = colorDomain
      .map<string>((v2) => {
        if (v2 === 'Active') return 'Available Space';
        if (typeof v2 !== 'string') return '';
        return `${v2} Listing`;
      })
      .filter(Boolean);
  } else if (
    [
      'Tenant',
      'Attraction',
      'StreetscapeAsset',
      'StreetscapeIssue',
      'Event',
      'Property',
      'Unit',
      'Building',
    ].includes(name)
  ) {
    colorDomain = getCategories(items);

    if (colorDomain.length === 0) {
      colorDomain = [pluralize(name)];
    } else {
      colorRange = getFillColorsForDomain(items, colorDomain);
      getStrokeItems(items).forEach((key) => {
        if (Array.isArray(key)) {
          const fn = addCircle(prefaceItems);
          fn(key);
        }
      });
    }
  } else if (colorDomain.length === 0) {
    colorDomain = [pluralize(name)];
  }

  let title = titleMap[name] || '';

  colorDomain = unique(colorDomain);

  return {
    title,
    type: 'category',
    dotStyle: 'fill',
    colorDomain,
    colorRange: _.compact(colorRange),
    prefaceItems,
  };
};

const transformDecks: MyReader<LegendT[]> = asks((e) => {
  let decks = e.decks;

  if (!decks) return [];

  const sig = `${decks.hashCode()}`;

  if (cache.has(sig)) {
    return cache.get(sig) || [];
  }
  let decksJS = decks.toJS();
  decksJS = ((decksJS: any): DeckT[]);
  let shouldShow = _.get(history.getQuery(), ['filter', 'type'], []);
  shouldShow = shouldShow.filter((item) =>
    [
      'Tenant',
      'Attraction',
      'StreetscapeAsset',
      'StreetscapeIssue',
      'Event',
      'Property',
      'Unit',
      'Building',
    ].includes(item)
  );
  let legendItems = deckToLayerList([
    ...decksJS,
    ...shouldShow.map((item) => ({
      page_type: item,
    })),
  ])
    .filter((v) => v.get('page_type'))
    .groupBy((v) => v.get('page_type') || 'undefined')
    .map(mkDeckLegendItem)
    .toList()
    .sortBy((v) => v.title || 'undefined')
    .toJS();
  legendItems = ((legendItems: any): LegendT[]);
  cache = cache.set(sig, legendItems);
  return legendItems;
});

const layerToLegendKeyMap = {
  name: 'title',
  legendType: 'type',
  legendMarkup: 'markup',
  legendColor: 'color',
  colorDomain: 'colorDomain',
  colorRange: 'colorRange',
  legendDotStyle: 'dotStyle',
  legendDotType: 'dotType',
  layergroup: null,
  uuid: 'uuid',
};
type LayerItem = {
  [key: $Keys<typeof layerToLegendKeyMap>]: any,
};
type LayerLegend = {
  [key: $Values<typeof layerToLegendKeyMap>]: any,
};

export const mkLayerLegendItem = (item: LayerItem): LayerLegend => {
  // Re-name the properties.
  const reducer = (s, key) => {
    const legendKey = layerToLegendKeyMap[key];
    if (!legendKey) return s;
    return { ...s, [String(legendKey)]: item[key] };
  };
  return Object.keys(item).reduce(reducer, { category_type: 'layer' });
};

const transformLayers: (legendArr: LegendT[]) => MyReader<LegendT[]> = (
  legendArr
) => {
  const reader = (e) => {
    if (!e.layers) return legendArr;
    let layers = e.layers.toJS();
    if (Array.isArray(layers)) {
      layers = ((layers: any): LayerT[]);
      const layerLegends = layers.map(mkLayerLegendItem);
      return legendArr.concat(layerLegends);
    }
    return [];
  };
  return asks(reader);
};

// Exported for testing
//
// Parameters:
//  opt - { layers: List<Map<>>, decks: List<Map<>> }
//
// Returns:
//  LegendT[]
//
export const program: Reader<Vars, Array<LegendT>> = transformDecks.chain<
  LegendT[]
>(transformLayers);

// Returns:
//  List<LegendT>
//
export default (): List<LegendT> => {
  const s = useContextState();
  let layers = I.List();
  let decks = s.decks.toList().map((v) => (v.toJS(): any));

  // Get the layer configs from loaded layers
  const layerIds = s.layersActiveLayers;
  layers = I.List(s.layersLoaded.filter((v) => layerIds.has(v.uuid)));
  const v = I.List(program.run({ decks, layers }));
  return ((v: any): List<LegendT>);
};
